import { BookOpen, Check, HelpCircle, Lightbulb, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

import { useTopicIsNew } from "../hooks";
import { getTopic, type HelpSide } from "../registry";
import { useHelpStore } from "../store";

interface Props {
  /** Identifiant du sujet dans `HELP_TOPICS`. */
  topic: string;
  side?: HelpSide;
  align?: "start" | "center" | "end";
  /** `icon` : un « ? » discret. `beacon` : point pulsant tant que le sujet est
   *  nouveau pour l'utilisateur, puis « ? ». `text` : lien textuel. */
  variant?: "icon" | "beacon" | "text";
  /** Libellé du variant `text`. */
  label?: string;
  className?: string;
  /** Contraste sur fond coloré (navbar). */
  onDark?: boolean;
}

/**
 * Bulle d'aide contextuelle : un déclencheur discret ouvre une carte qui
 * explique l'élément voisin — titre, texte, marche à suivre, astuce, renvoi
 * vers la section du guide. « Compris » éteint la balise pour cet
 * utilisateur, sur ce poste ; le « ? » reste toujours accessible.
 *
 * Un sujet inconnu ne rend rien : une clé mal orthographiée ne casse jamais
 * une page.
 */
export function HelpBubble({
  topic: topicId,
  side = "bottom",
  align = "center",
  variant = "beacon",
  label,
  className,
  onDark,
}: Props) {
  const topic = getTopic(topicId);
  const isNew = useTopicIsNew(topicId);
  const dismiss = useHelpStore((s) => s.dismiss);
  const [open, setOpen] = useState(false);
  if (!topic) return null;

  const pulse = variant === "beacon" && isNew;
  const baseBtn = cn(
    "relative inline-flex shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
    onDark
      ? "text-primary-foreground/85 hover:text-primary-foreground focus-visible:ring-primary-foreground/60"
      : "text-muted hover:text-primary focus-visible:ring-primary",
  );

  let trigger: ReactNode;
  if (variant === "text") {
    trigger = (
      <button
        type="button"
        className={cn(
          "inline-flex cursor-pointer items-center gap-1 text-xs font-medium underline underline-offset-2",
          onDark ? "text-primary-foreground/90" : "text-primary",
          className,
        )}
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        {label ?? "Aide"}
      </button>
    );
  } else {
    trigger = (
      <button
        type="button"
        aria-label={`Aide : ${topic.title}`}
        title={topic.title}
        className={cn(baseBtn, "h-6 w-6 cursor-pointer", className)}
      >
        <HelpCircle className="h-4 w-4" aria-hidden />
        {pulse && (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5" aria-hidden>
            <span
              className={cn(
                "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75",
                onDark ? "bg-primary-foreground" : "bg-primary",
              )}
            />
            <span
              className={cn(
                "relative inline-flex h-2.5 w-2.5 rounded-full",
                onDark ? "bg-primary-foreground" : "bg-primary",
              )}
            />
          </span>
        )}
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={12}
        className="w-[340px] p-0 text-foreground shadow-lg"
        data-help-bubble={topic.id}
      >
        <HelpCard topic={topic} onClose={() => setOpen(false)} onGotIt={() => { dismiss(topic.id); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

/** Corps d'une bulle — partagé avec la carte des visites guidées. */
export function HelpCard({
  topic,
  onClose,
  onGotIt,
  footer,
  compact,
}: {
  topic: NonNullable<ReturnType<typeof getTopic>>;
  onClose?: () => void;
  onGotIt?: () => void;
  /** Remplace le pied par défaut (navigation de visite). */
  footer?: ReactNode;
  compact?: boolean;
}) {
  // Le guide est réservé aux connectés (ADR 0046) : hors session
  // (inscription), la bulle se suffit à elle-même, sans lien « En savoir plus ».
  const signedIn = useAuthStore((s) => !!s.me);
  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-2 border-b border-border bg-primary/5 px-4 py-3">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center bg-primary text-primary-foreground">
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </span>
        <h3 className="flex-1 text-sm font-semibold leading-snug text-foreground">{topic.title}</h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer l'aide"
            className="-mr-1 -mt-1 grid h-7 w-7 cursor-pointer place-items-center text-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <div className={cn("space-y-3 px-4 py-3 text-sm", compact && "py-2.5")}>
        <p className="text-muted-foreground">{topic.body}</p>
        {topic.steps && topic.steps.length > 0 && (
          <ol className="space-y-1.5">
            {topic.steps.map((s, i) => (
              <li key={s} className="flex gap-2 text-foreground">
                <span className="mt-px grid h-5 w-5 shrink-0 place-items-center bg-primary/10 text-[11px] font-semibold text-primary">
                  {i + 1}
                </span>
                <span className="leading-snug">{s}</span>
              </li>
            ))}
          </ol>
        )}
        {topic.tip && (
          <p className="flex gap-2 border-l-2 border-warning bg-warning/10 px-2.5 py-2 text-xs text-foreground">
            <Lightbulb className="mt-px h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            <span>{topic.tip}</span>
          </p>
        )}
      </div>
      {footer ?? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
          {topic.guide && signedIn ? (
            <Link
              to={`/aide#${topic.guide}`}
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden /> En savoir plus
            </Link>
          ) : (
            <span />
          )}
          {onGotIt && (
            <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={onGotIt}>
              <Check className="h-3.5 w-3.5" aria-hidden /> Compris
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
